"use client";

import React, {
  createContext,
  ReactNode,
  useContext,
  useEffect,
  useMemo,
  useRef,
} from "react";

import { COLORS, Theme, THEMES } from "../constants";
import { transparentize } from "color2k";
import {
  DEFAULT_WHITE_BACKGROUND,
  Palette,
} from "../constants/palettes";
import { LIGHT_MODE } from "../constants/palettes";

interface ThemeContextType {
  theme: Theme;
  className: string;
  style: ThemeVariables;
  palette: Palette;
}

const ThemeContext = createContext<ThemeContextType | undefined>(undefined);

export const useTheme = () => {
  const context = useContext(ThemeContext);
  if (context === undefined) {
    throw new Error("useTheme must be used within a ThemeProvider");
  }
  return context;
};

interface ThemeProviderProps {
  theme: Theme;
  children: ReactNode;
  palette?: Palette;
}

const THEME_CLASSES = {
  [THEMES.DARK]: "proton-ui__theme--dark",
  [THEMES.CUSTOM_DARK]: "proton-ui__theme--custom-dark",
  [THEMES.LIGHT]: "proton-ui__theme--light",
};

export const ThemeProvider: React.FC<ThemeProviderProps> = ({
  theme,
  children,
  palette = LIGHT_MODE,
}) => {
  const containerRef = useRef<HTMLDivElement>(null);
  const themeClass = THEME_CLASSES[theme];
  const themeVariables = useMemo(
    () => generateThemeCssVariables(palette, theme),
    [palette, theme]
  );

  useEffect(() => {
    // Apply the theme class and css variables to the container div so that it is only scoped
    // to the ThemeProvider children.
    if (containerRef.current) {
      containerRef.current.className = themeClass;

      Object.entries(themeVariables).forEach(([key, value]) => {
        containerRef.current!.style.setProperty(key, value as string);
      });
    }
  }, [theme, themeClass, palette, themeVariables]);

  return (
    <ThemeContext.Provider
      value={{
        theme,
        style: themeVariables,
        palette,
        className: themeClass,
      }}
    >
      <div ref={containerRef}>{children}</div>
    </ThemeContext.Provider>
  );
};

export interface ThemeVariables {
  "--proton-control__background-color": string;
  "--proton-control__background-color-light": string;
  "--proton-control__text-color": string;
  "--proton-control__border-color": string;
  "--proton-control__shadow-color": string;

  //brand colors
  "--proton-color__primary": string;
  "--proton-color__primary-light": string;
  "--proton-color__secondary": string;

  //primary colors
  "--proton-control__primary-super-dark": string;
  "--proton-control__primary-dark": string;
  "--proton-control__primary-medium": string;
  "--proton-control__primary-medium-light": string;
  "--proton-control__primary-light": string;
  "--proton-control__primary-lightest": string;
  "--proton-control__primary-super-light": string;

  //secondary colors
  "--proton-control__secondary-super-dark": string;
  "--proton-control__secondary-dark": string;
  "--proton-control__secondary-medium": string;
  "--proton-control__secondary-medium-light": string;
  "--proton-control__secondary-light": string;
  "--proton-control__secondary-lightest": string;
  "--proton-control__secondary-super-light": string;
}

const setDefaultCssVariables = (palette: Palette, theme: Theme) => {
  const grayScaleVariables =
    theme === THEMES.DARK || theme === THEMES.CUSTOM_DARK
      ? {
          "--proton-color__gray-super-dark":
            COLORS.DARK_GRAYSCALE.GRAY_SUPER_DARK,
          "--proton-color__gray-dark": COLORS.DARK_GRAYSCALE.GRAY_DARK,
          "--proton-color__gray-medium": COLORS.DARK_GRAYSCALE.GRAY_MEDIUM,
          "--proton-color__gray-medium-light":
            COLORS.DARK_GRAYSCALE.GRAY_MEDIUM_LIGHT,
          "--proton-color__gray-light": COLORS.DARK_GRAYSCALE.GRAY_LIGHT,
          "--proton-color__gray-lightest": COLORS.DARK_GRAYSCALE.GRAY_LIGHTEST,
          "--proton-color__gray-super-light":
            COLORS.DARK_GRAYSCALE.GRAY_SUPER_LIGHT,
        }
      : {
          "--proton-color__gray-super-dark": COLORS.GRAYSCALE.GRAY_SUPER_DARK,
          "--proton-color__gray-dark": COLORS.GRAYSCALE.GRAY_DARK,
          "--proton-color__gray-medium": COLORS.GRAYSCALE.GRAY_MEDIUM,
          "--proton-color__gray-medium-light":
            COLORS.GRAYSCALE.GRAY_MEDIUM_LIGHT,
          "--proton-color__gray-light": COLORS.GRAYSCALE.GRAY_LIGHT,
          "--proton-color__gray-lightest": COLORS.GRAYSCALE.GRAY_LIGHTEST,
          "--proton-color__gray-super-light": COLORS.GRAYSCALE.GRAY_SUPER_LIGHT,
        };

  return {
    //brand colors
    "--proton-color__primary": palette.BRAND.PRIMARY,
    "--proton-color__primary-light": palette.BRAND.PRIMARY_LIGHT,
    "--proton-color__secondary": palette.BRAND.SECONDARY,

    //primary colors
    "--proton-control__primary-super-dark": palette.PRIMARY.PRIMARY_SUPER_DARK,
    "--proton-control__primary-dark": palette.PRIMARY.PRIMARY_DARK,
    "--proton-control__primary-medium": palette.PRIMARY.PRIMARY_MEDIUM,
    "--proton-control__primary-medium-light":
      palette.PRIMARY.PRIMARY_MEDIUM_LIGHT,
    "--proton-control__primary-light": palette.PRIMARY.PRIMARY_LIGHT,
    "--proton-control__primary-lightest": palette.PRIMARY.PRIMARY_LIGHTEST,
    "--proton-control__primary-super-light":
      palette.PRIMARY.PRIMARY_SUPER_LIGHT,

    //secondary colors
    "--proton-control__secondary-super-dark":
      palette.SECONDARY.SECONDARY_SUPER_DARK,
    "--proton-control__secondary-dark": palette.SECONDARY.SECONDARY_DARK,
    "--proton-control__secondary-medium": palette.SECONDARY.SECONDARY_MEDIUM,
    "--proton-control__secondary-medium-light":
      palette.SECONDARY.SECONDARY_MEDIUM_LIGHT,
    "--proton-control__secondary-light": palette.SECONDARY.SECONDARY_LIGHT,
    "--proton-control__secondary-lightest":
      palette.SECONDARY.SECONDARY_LIGHTEST,
    "--proton-control__secondary-super-light":
      palette.SECONDARY.SECONDARY_SUPER_LIGHT,

    ...grayScaleVariables,
  };
};

function generateThemeCssVariables(
  palette: Palette,
  theme: Theme
): ThemeVariables {
  const defaultVars = setDefaultCssVariables(palette, theme);

  switch (theme) {
    case THEMES.DARK:
      return {
        "--proton-control__background-color": COLORS.DARK_GRAYSCALE.GRAY_DARK,
        "--proton-control__background-color-light":
          COLORS.DARK_GRAYSCALE.ELEVATION,
        "--proton-control__text-color": COLORS.DARK_GRAYSCALE.WHITE,
        "--proton-control__border-color": COLORS.DARK_GRAYSCALE.GRAY_LIGHT,
        "--proton-control__shadow-color": transparentize(
          COLORS.DARK_GRAYSCALE.GRAY_LIGHTEST,
          0.5
        ),
        ...defaultVars,
      };

    case THEMES.LIGHT:
      return {
        "--proton-control__background-color": DEFAULT_WHITE_BACKGROUND,
        "--proton-control__background-color-light": COLORS.GRAYSCALE.ELEVATION,
        "--proton-control__text-color": COLORS.GRAYSCALE.GRAY_DARK,
        "--proton-control__border-color": COLORS.GRAYSCALE.BORDER,
        "--proton-control__shadow-color": transparentize(
          COLORS.GRAYSCALE.GRAY_LIGHTEST,
          0.5
        ),
        ...defaultVars,
      };

    case THEMES.CUSTOM_DARK:
      return {
        "--proton-control__background-color": COLORS.DARK_GRAYSCALE.GRAY_DARK,
        "--proton-control__background-color-light": "rgba(255, 255, 255, 0.2)",
        "--proton-control__text-color": COLORS.DARK_GRAYSCALE.WHITE,
        "--proton-control__border-color": COLORS.DARK_GRAYSCALE.BORDER,
        "--proton-control__shadow-color": transparentize(
          COLORS.DARK_GRAYSCALE.GRAY_LIGHTEST,
          0.7
        ),
        ...defaultVars,
      };

    default:
      return {
        "--proton-control__background-color": DEFAULT_WHITE_BACKGROUND,
        "--proton-control__background-color-light": COLORS.GRAYSCALE.ELEVATION,
        "--proton-control__text-color": COLORS.GRAYSCALE.WHITE,
        "--proton-control__border-color": COLORS.GRAYSCALE.GRAY_DARK,
        "--proton-control__shadow-color": COLORS.GRAYSCALE.GRAY_DARK,
        ...defaultVars,
      };
  }
}
